import { useState, useMemo } from "react";
import { FileText, Plus, Search, AlertTriangle, CheckCircle, Clock, XCircle } from "lucide-react";
import Badge from "../components/Badge";
import GuideBanner from "../components/GuideBanner";
import { CONTRACT_STATUSES } from "../constants";
import { fmt, fmtDate, fmtShort } from "../utils";

const STATUS_ICONS = {
  rascunho: <FileText size={13} />,
  ativo: <CheckCircle size={13} />,
  vencendo: <AlertTriangle size={13} />,
  vencido: <XCircle size={13} />,
  cancelado: <XCircle size={13} />,
};

export default function Contracts({
  contracts, clients, today, search,
  setShowNewContract, setSelContract,
}) {
  const [statusFilter, setStatusFilter] = useState("all");

  // Auto-detect expiring & expired
  const enriched = useMemo(() => contracts.map((c) => {
    let status = c.status;
    if (status === "ativo" && c.endDate) {
      const daysLeft = Math.ceil((new Date(c.endDate) - new Date(today)) / 86400000);
      if (daysLeft < 0) status = "vencido";
      else if (daysLeft <= 30) status = "vencendo";
    }
    return { ...c, computedStatus: status };
  }), [contracts, today]);

  const filtered = useMemo(() => {
    let list = enriched;
    if (statusFilter !== "all") list = list.filter((c) => c.computedStatus === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c) =>
        (c.clientName || "").toLowerCase().includes(q) ||
        (c.notes || "").toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => (a.endDate || "").localeCompare(b.endDate || ""));
  }, [enriched, statusFilter, search]);

  // KPIs
  const kpis = useMemo(() => {
    const active = enriched.filter((c) => c.computedStatus === "ativo");
    const expiring = enriched.filter((c) => c.computedStatus === "vencendo");
    const expired = enriched.filter((c) => c.computedStatus === "vencido");
    const mrr = active.reduce((s, c) => s + (parseFloat(c.value) || 0), 0) +
                expiring.reduce((s, c) => s + (parseFloat(c.value) || 0), 0);
    return { total: enriched.length, active: active.length, expiring: expiring.length, expired: expired.length, mrr };
  }, [enriched]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <GuideBanner
        title="Gestão de Contratos"
        text="Acompanhe todos os contratos ativos, vencendo e vencidos. Crie contratos a partir de clientes ganhos para formalizar a relação comercial."
      />

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        {[
          { label: "Total Contratos", value: kpis.total, color: "#251C59" },
          { label: "Ativos", value: kpis.active, color: "#059669" },
          { label: "Vencendo (30d)", value: kpis.expiring, color: "#f59e0b" },
          { label: "Vencidos", value: kpis.expired, color: "#ef4444" },
          { label: "Receita Mensal", value: fmt(kpis.mrr), color: "#005BED" },
        ].map((k, i) => (
          <div key={i} style={{ background: "#fff", borderRadius: 10, padding: "14px 16px", border: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>{k.value}</div>
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 1 }}>{k.label}</div>
            <div style={{ height: 3, borderRadius: 2, background: k.color, marginTop: 8, opacity: 0.6 }} />
          </div>
        ))}
      </div>

      {/* Filters + Add */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 500,
            border: "1px solid #e2e8f0", background: "rgba(128,128,128,0.08)",
            color: "#555", fontFamily: "inherit",
          }}
        >
          <option value="all">Todos os Status</option>
          {CONTRACT_STATUSES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setShowNewContract(true)}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 16px", borderRadius: 8, border: "none",
            background: "#251C59", color: "#fff", fontSize: 12,
            fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          <Plus size={14} /> Novo Contrato
        </button>
      </div>

      {/* Alerts: expiring */}
      {kpis.expiring > 0 && (
        <div style={{
          background: "#fffbeb", borderRadius: 10, padding: "12px 16px",
          border: "1px solid #fde68a", display: "flex", gap: 8, alignItems: "center",
        }}>
          <AlertTriangle size={16} color="#f59e0b" />
          <span style={{ fontSize: 12, color: "#92400e", fontWeight: 600 }}>
            {kpis.expiring} contrato(s) vencem nos próximos 30 dias — programe a renovação!
          </span>
        </div>
      )}
      {kpis.expired > 0 && (
        <div style={{
          background: "#fef2f2", borderRadius: 10, padding: "12px 16px",
          border: "1px solid #fecaca", display: "flex", gap: 8, alignItems: "center",
        }}>
          <XCircle size={16} color="#ef4444" />
          <span style={{ fontSize: 12, color: "#991b1b", fontWeight: 600 }}>
            {kpis.expired} contrato(s) VENCIDO(S) — providencie renovação ou cancelamento.
          </span>
        </div>
      )}

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              {["Cliente", "Tipo", "Valor/Mês", "Início", "Vencimento", "Renovação", "Status", ""].map((h, i) => (
                <th key={i} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, color: "#64748b", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid #e5e7eb" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Nenhum contrato encontrado</td></tr>
            ) : (
              filtered.map((c) => {
                const st = CONTRACT_STATUSES.find((s) => s.id === c.computedStatus);
                return (
                  <tr key={c.id} onClick={() => setSelContract(c)} style={{ cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 600, color: "#0f172a" }}>{c.clientName || "—"}</td>
                    <td style={{ padding: "10px 12px", color: "#475569" }}>
                      {c.type === "mensal" ? "Mensal" : c.type === "anual" ? "Anual" : c.type === "semestral" ? "Semestral" : c.type}
                    </td>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: "#0f172a" }}>{fmt(c.value || 0)}</td>
                    <td style={{ padding: "10px 12px", color: "#475569" }}>{fmtShort(c.startDate)}</td>
                    <td style={{ padding: "10px 12px", color: c.computedStatus === "vencido" ? "#ef4444" : c.computedStatus === "vencendo" ? "#f59e0b" : "#475569", fontWeight: c.computedStatus === "vencido" || c.computedStatus === "vencendo" ? 700 : 400 }}>
                      {fmtShort(c.endDate)}
                    </td>
                    <td style={{ padding: "10px 12px", color: "#475569" }}>{fmtShort(c.renewalDate)}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <Badge color={st?.color}>{st?.label || c.computedStatus}</Badge>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ color: "#94a3b8", fontSize: 11 }}>→</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
